import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
    FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt,
    FaVenusMars, FaSave, FaTimes, FaTrash, FaUserPlus, FaLock,
    FaIdCard, FaHome, FaCity, FaGlobe, FaInfoCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RoleService from '../../../services/RoleService';
import HabilitationService from '../../../services/HabilitationService';
import UserService from '../../../services/UserService';

// Composant DisplayField extrait en dehors du composant principal
const DisplayField = memo(({ label, value, icon, name, type = 'text', error, isValid, disabled, onChange, placeholder }) => {
    const fieldId = `field-${name}`;

    const getBorderColor = () => {
        if (error) return '2px solid #dc3545';
        if (isValid) return '2px solid #28a745';
        return '1px solid #ced4da';
    };

    const commonStyles = {
        width: '100%',
        border: getBorderColor(),
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '0.95rem',
        color: '#1a202c',
        backgroundColor: '#fff',
        transition: 'border-color 0.2s ease'
    };

    const renderInput = () => {
        const inputProps = {
            id: fieldId,
            name: name,
            value: value || '',
            onChange: onChange,
            disabled: disabled,
            style: commonStyles,
            className: 'field-value',
            placeholder: placeholder || `Entrez ${label.toLowerCase()}`
        };

        if (type === 'textarea') {
            return <textarea {...inputProps} rows={3} />;
        }

        if (type === 'select') {
            return (
                <select {...inputProps}>
                    <option value="">Sélectionnez un genre</option>
                    <option value="MALE">Masculin</option>
                    <option value="FEMALE">Féminin</option>
                </select>
            );
        }

        return <input {...inputProps} type={type} />;
    };

    return (
        <div className="display-field mb-3">
            <div className="field-label" style={{
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '0.5rem'
            }}>
                {icon} {label}
            </div>
            {renderInput()}
            {error && (
                <div className="invalid-feedback" style={{
                    color: '#dc3545',
                    fontSize: '0.875em',
                    marginTop: '0.25rem'
                }}>
                    {error}
                </div>
            )}
        </div>
    );
});

DisplayField.displayName = 'DisplayField';

const CreateClinetModal = ({ onClose, onCreate }) => {
    const [newUser, setNewUser] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        neighborhood: '',
        city: '',
        country: '',
        dateOfBirth: '',
        gender: '',
        username: '',
        password: '',
        profilePictureUrl: '',
        additionalInfos: '',
        roleIds: [],
        habilitationNames: []
    });

    const [allRoles, setAllRoles] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [allHabilitations, setAllHabilitations] = useState([]);
    const [availableHabilitations, setAvailableHabilitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [formSubmitted, setFormSubmitted] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [rolesResponse, habilitationsResponse] = await Promise.all([
                    RoleService.findAllRoles(),
                    HabilitationService.getAllHabilitations()
                ]);

                setAllRoles(rolesResponse);
                setAvailableRoles(rolesResponse);
                setAllHabilitations(habilitationsResponse);
                setAvailableHabilitations(habilitationsResponse);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Erreur lors du chargement des données initiales');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Utilisation de useCallback pour éviter la recréation de la fonction
    const validateField = useCallback((name, value) => {
        let error = '';

        switch (name) {
            case 'firstName':
            case 'lastName':
                if (!value?.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'email':
                if (!value?.trim()) {
                    error = 'Ce champ est obligatoire';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Email invalide';
                }
                break;
            case 'phoneNumber':
                if (value && !/^[0-9+\s-]+$/.test(value)) {
                    error = 'Numéro de téléphone invalide';
                }
                break;
            case 'username':
            case 'password':
            case 'neighborhood':
            case 'city':
            case 'country':
                if (!value?.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'dateOfBirth':
                if (!value) {
                    error = 'Ce champ est obligatoire';
                } else {
                    const today = new Date();
                    const birthDate = new Date(value);
                    if (birthDate > today) {
                        error = 'La date ne peut pas être dans le futur';
                    }
                }
                break;
            case 'gender':
                if (!value) error = 'Ce champ est obligatoire';
                break;
            default:
                break;
        }

        return error;
    }, []);

    // Utilisation de useCallback pour éviter la recréation de la fonction
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;

        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));

        // Validation en temps réel
        const error = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    }, [validateField]);

    const handleRoleSelect = useCallback((e) => {
        const selectedRoleId = parseInt(e.target.value);
        if (selectedRoleId === 0) return;

        const selectedRole = allRoles.find(role => role.id === selectedRoleId);

        if (selectedRole && !newUser.roleIds.includes(selectedRoleId)) {
            setNewUser(prev => ({
                ...prev,
                roleIds: [...prev.roleIds, selectedRoleId]
            }));

            setAvailableRoles(prev =>
                prev.filter(role => role.id !== selectedRoleId)
            );

            setErrors(prev => ({
                ...prev,
                roles: undefined
            }));
        }
    }, [allRoles, newUser.roleIds]);

    const removeRole = useCallback((roleId) => {
        setNewUser(prev => ({
            ...prev,
            roleIds: prev.roleIds.filter(id => id !== roleId)
        }));

        const roleToAddBack = allRoles.find(role => role.id === roleId);
        if (roleToAddBack) {
            setAvailableRoles(prev => [...prev, roleToAddBack]);
        }
    }, [allRoles]);

    const handleHabilitationSelect = useCallback((e) => {
        const selectedHabilitationName = e.target.value;
        if (selectedHabilitationName === "") return;

        const selectedHabilitation = allHabilitations.find(
            h => h.name === selectedHabilitationName
        );

        if (selectedHabilitation && !newUser.habilitationNames.includes(selectedHabilitationName)) {
            setNewUser(prev => ({
                ...prev,
                habilitationNames: [...prev.habilitationNames, selectedHabilitationName]
            }));

            setAvailableHabilitations(prev =>
                prev.filter(h => h.name !== selectedHabilitationName)
            );
        }
    }, [allHabilitations, newUser.habilitationNames]);

    const removeHabilitation = useCallback((habilitationName) => {
        setNewUser(prev => ({
            ...prev,
            habilitationNames: prev.habilitationNames.filter(name => name !== habilitationName)
        }));

        const habilitationToAddBack = allHabilitations.find(h => h.name === habilitationName);
        if (habilitationToAddBack) {
            setAvailableHabilitations(prev => [...prev, habilitationToAddBack]);
        }
    }, [allHabilitations]);

    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        Object.keys(newUser).forEach(field => {
            if (field !== 'profilePictureUrl' && field !== 'additionalInfos' && field !== 'habilitationNames') {
                const error = validateField(field, newUser[field]);
                if (error) {
                    newErrors[field] = error;
                    isValid = false;
                }
            }
        });

        if (newUser.roleIds.length === 0) {
            newErrors.roles = 'Au moins un rôle doit être sélectionné';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    }, [newUser, validateField]);

    const handleSubmit = useCallback(async () => {
        setFormSubmitted(true);

        if (!validateForm()) {
            const firstErrorField = Object.keys(errors).find(key => errors[key]);
            if (firstErrorField) {
                const element = document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.focus();
                } else if (firstErrorField === 'roles') {
                    document.querySelector('.roles-section select')?.focus();
                }
            }
            return;
        }

        setSubmitting(true);

        try {
            let formattedDateOfBirth = '';
            if (newUser.dateOfBirth) {
                const date = new Date(newUser.dateOfBirth);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                formattedDateOfBirth = `${day}-${month}-${year} 00:00`;
            }

            const userWithAdditionalFields = {
                ...newUser,
                dateOfBirth: formattedDateOfBirth || null,
                createdBy: 'admin',
                lastModifiedBy: 'admin',
            };

            const createdUser = await UserService.createUser(userWithAdditionalFields);

            toast.success('Utilisateur créé avec succès');
            onCreate(createdUser);
            onClose();
        } catch (error) {
            console.error('Error creating user:', error);

            let errorMessage = 'Erreur lors de la création de l\'utilisateur';
            if (error.response) {
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 404) {
                    errorMessage = 'Endpoint non trouvé - Vérifiez l\'URL de l\'API';
                } else if (error.response.data) {
                    errorMessage = JSON.stringify(error.response.data);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    }, [newUser, validateForm, errors, onCreate, onClose]);

    if (loading) {
        return (
            <div className="contract-modal-overlay show" style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                <div className="text-center text-white">
                    <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-3">Chargement en cours...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Modal principal */}
            <div
                className="contract-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="contract-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaUserPlus className="me-2 text-primary" />
                                    Créer un nouvel utilisateur
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    Remplissez tous les champs obligatoires pour créer un compte utilisateur
                                </p>
                            </div>
                            <button
                                className="btn-close"
                                onClick={onClose}
                                disabled={submitting}
                            ></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Informations personnelles */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaUser className="me-2" />
                                Informations personnelles
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Prénom"
                                        value={newUser.firstName}
                                        icon={<FaUser className="text-primary" />}
                                        name="firstName"
                                        error={errors.firstName}
                                        isValid={formSubmitted && newUser.firstName && !errors.firstName}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Nom"
                                        value={newUser.lastName}
                                        icon={<FaUser className="text-primary" />}
                                        name="lastName"
                                        error={errors.lastName}
                                        isValid={formSubmitted && newUser.lastName && !errors.lastName}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Email"
                                        value={newUser.email}
                                        icon={<FaEnvelope className="text-primary" />}
                                        name="email"
                                        type="email"
                                        error={errors.email}
                                        isValid={formSubmitted && newUser.email && !errors.email}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Téléphone"
                                        value={newUser.phoneNumber}
                                        icon={<FaPhone className="text-primary" />}
                                        name="phoneNumber"
                                        type="tel"
                                        error={errors.phoneNumber}
                                        isValid={formSubmitted && newUser.phoneNumber && !errors.phoneNumber}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Date de naissance"
                                        value={newUser.dateOfBirth}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                        name="dateOfBirth"
                                        type="date"
                                        error={errors.dateOfBirth}
                                        isValid={formSubmitted && newUser.dateOfBirth && !errors.dateOfBirth}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="display-field mb-3">
                                        <div className="field-label" style={{
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <FaVenusMars className="text-primary" /> Genre
                                        </div>
                                        <select
                                            name="gender"
                                            value={newUser.gender}
                                            onChange={handleInputChange}
                                            disabled={submitting}
                                            style={{
                                                width: '100%',
                                                border: errors.gender ? '2px solid #dc3545' :
                                                    (formSubmitted && newUser.gender && !errors.gender) ? '2px solid #28a745' : '1px solid #ced4da',
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                fontSize: '0.95rem',
                                                color: '#1a202c',
                                                backgroundColor: '#fff',
                                                transition: 'border-color 0.2s ease'
                                            }}
                                            className="field-value"
                                        >
                                            <option value="">Sélectionnez un genre</option>
                                            <option value="MALE">Masculin</option>
                                            <option value="FEMALE">Féminin</option>
                                        </select>
                                        {errors.gender && (
                                            <div className="invalid-feedback" style={{
                                                color: '#dc3545',
                                                fontSize: '0.875em',
                                                marginTop: '0.25rem'
                                            }}>
                                                {errors.gender}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Informations de connexion */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">
                                <FaLock className="me-2" />
                                Informations de connexion
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Nom d'utilisateur"
                                        value={newUser.username}
                                        icon={<FaUser className="text-success" />}
                                        name="username"
                                        error={errors.username}
                                        isValid={formSubmitted && newUser.username && !errors.username}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Mot de passe"
                                        value={newUser.password}
                                        icon={<FaLock className="text-success" />}
                                        name="password"
                                        type="password"
                                        error={errors.password}
                                        isValid={formSubmitted && newUser.password && !errors.password}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Localisation */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-warning">
                                <FaMapMarkerAlt className="me-2" />
                                Localisation
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <DisplayField
                                        label="Quartier"
                                        value={newUser.neighborhood}
                                        icon={<FaHome className="text-warning" />}
                                        name="neighborhood"
                                        error={errors.neighborhood}
                                        isValid={formSubmitted && newUser.neighborhood && !errors.neighborhood}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <DisplayField
                                        label="Ville"
                                        value={newUser.city}
                                        icon={<FaCity className="text-warning" />}
                                        name="city"
                                        error={errors.city}
                                        isValid={formSubmitted && newUser.city && !errors.city}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <DisplayField
                                        label="Pays"
                                        value={newUser.country}
                                        icon={<FaGlobe className="text-warning" />}
                                        name="country"
                                        error={errors.country}
                                        isValid={formSubmitted && newUser.country && !errors.country}
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Rôles */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaIdCard className="me-2" />
                                Rôles
                            </h5>
                            {errors.roles && newUser.roleIds.length === 0 && (
                                <div className="alert alert-danger" style={{
                                    color: '#721c24',
                                    backgroundColor: '#f8d7da',
                                    borderColor: '#f5c6cb',
                                    padding: '0.75rem 1.25rem',
                                    marginBottom: '1rem',
                                    border: '1px solid transparent',
                                    borderRadius: '0.25rem'
                                }}>
                                    {errors.roles}
                                </div>
                            )}
                            <div className="display-field mb-3">
                                <div className="field-label" style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: '#2c3e50',
                                    marginBottom: '0.5rem'
                                }}>
                                    <FaIdCard className="text-info" /> Sélectionner un rôle
                                </div>
                                <select
                                    onChange={handleRoleSelect}
                                    defaultValue={0}
                                    disabled={submitting || availableRoles.length === 0}
                                    style={{
                                        width: '100%',
                                        border: errors.roles ? '2px solid #dc3545' :
                                            newUser.roleIds.length > 0 ? '2px solid #28a745' : '1px solid #ced4da',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        fontSize: '0.95rem',
                                        color: '#1a202c',
                                        backgroundColor: '#fff',
                                        transition: 'border-color 0.2s ease'
                                    }}
                                    className="field-value"
                                >
                                    <option value={0}>Sélectionnez un rôle</option>
                                    {availableRoles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.name} - {role.description}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="selected-items" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {newUser.roleIds.map(roleId => {
                                    const role = allRoles.find(r => r.id === roleId);
                                    return role ? (
                                        <div key={roleId} className="selected-item" style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '0.375rem 0.75rem',
                                            backgroundColor: '#e9ecef',
                                            borderRadius: '20px',
                                            fontSize: '0.9rem'
                                        }}>
                                            <span className="me-2">{role.name}</span>
                                            <button
                                                onClick={() => removeRole(roleId)}
                                                disabled={submitting}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#dc3545',
                                                    cursor: 'pointer',
                                                    padding: '0 4px'
                                                }}
                                            >
                                                <FaTrash size={12} />
                                            </button>
                                        </div>
                                    ) : null;
                                })}
                            </div>
                        </div>

                        {/* Habilitations */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-secondary">
                                <FaInfoCircle className="me-2" />
                                Habilitations
                            </h5>
                            <div className="display-field mb-3">
                                <div className="field-label" style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: '#2c3e50',
                                    marginBottom: '0.5rem'
                                }}>
                                    <FaInfoCircle className="text-secondary" /> Sélectionner une habilitation
                                </div>
                                <select
                                    onChange={handleHabilitationSelect}
                                    defaultValue=""
                                    disabled={submitting || availableHabilitations.length === 0}
                                    style={{
                                        width: '100%',
                                        border: '1px solid #ced4da',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        fontSize: '0.95rem',
                                        color: '#1a202c',
                                        backgroundColor: '#fff',
                                        transition: 'border-color 0.2s ease'
                                    }}
                                    className="field-value"
                                >
                                    <option value="">Sélectionnez une habilitation</option>
                                    {availableHabilitations.map(habilitation => (
                                        <option key={habilitation.name} value={habilitation.name}>
                                            {habilitation.name} ({habilitation.type} - {habilitation.action})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="selected-items" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {newUser.habilitationNames.map(name => (
                                    <div key={name} className="selected-item" style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        padding: '0.375rem 0.75rem',
                                        backgroundColor: '#e9ecef',
                                        borderRadius: '20px',
                                        fontSize: '0.9rem'
                                    }}>
                                        <span className="me-2">{name}</span>
                                        <button
                                            onClick={() => removeHabilitation(name)}
                                            disabled={submitting}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#dc3545',
                                                cursor: 'pointer',
                                                padding: '0 4px'
                                            }}
                                        >
                                            <FaTrash size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Informations supplémentaires */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-danger">
                                <FaInfoCircle className="me-2" />
                                Informations supplémentaires
                            </h5>
                            <div className="row g-3">
                                <div className="col-12">
                                    <DisplayField
                                        label="URL de la photo de profil"
                                        value={newUser.profilePictureUrl}
                                        icon={<FaUser className="text-danger" />}
                                        name="profilePictureUrl"
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-12">
                                    <DisplayField
                                        label="Informations supplémentaires"
                                        value={newUser.additionalInfos}
                                        icon={<FaInfoCircle className="text-danger" />}
                                        name="additionalInfos"
                                        type="textarea"
                                        disabled={submitting}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer fixe avec boutons d'action */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px'
                    }}>
                        <div className="d-flex justify-content-end gap-3">
                            <button
                                onClick={onClose}
                                className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                disabled={submitting}
                            >
                                <FaTimes /> Annuler
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Création en cours...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> Créer l'utilisateur
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .contract-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-value:focus {
                    outline: none;
                    border-color: #0d6efd !important;
                    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
                }

                .field-value:disabled {
                    background-color: #e9ecef;
                    cursor: not-allowed;
                }
            `}</style>
        </>
    );
};

export default CreateClinetModal;